import Button from "@mui/material/Button";
import SearchMatch from "_shared/_views/tables/search-match";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import type { MRT_RowData, MRT_TableOptions } from "material-react-table";
import type { MouseEvent } from "react";
import { ROW_ALREADY_CHOSEN_LABEL, useRowChoiceLabel } from "_shared/_views/tables/row-choice-label";

/**
 * The part of a picker's row that says the row chooses something.
 *
 * `RowLink` is how a row that OPENS a record says so, and pickers were left out of it (#5145): a row
 * that chooses points nowhere, so there is no link to draw. That left the people pickers saying
 * "click me" with `cursor: pointer` alone — the signal #5122 showed nobody receives until they are
 * already hovering. A picker's row now ends in a button that names the choice, and the row itself
 * stays clickable as the larger target. The button's words are the host's: see `RowChoiceLabel`.
 */

const PersonCell = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));
const PersonText = styled("div")(() => ({
  display: "grid",
  minWidth: 0,
}));
const PersonName = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
})) as typeof Typography;
const PersonDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
})) as typeof Typography;
// Wraps rather than ellipsizing like the details above it: a reason cut off halfway explains nothing.
const PersonUnavailableReason = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
})) as typeof Typography;

/**
 * Why a row cannot be chosen, said as text under the person's name. A disabled button takes no
 * keyboard focus, so a reason kept in a tooltip would reach only a mouse; `id` is what the button's
 * `describedBy` names, so a screen reader hears the reason with the button.
 */
export type RowChoiceUnavailableReason = { id: string; text: string };

/**
 * A line under a person's name. One the picker's search reads is `{ text, searched: true }`, so it is
 * marked like the name (#6590); a plain string is never marked, so a mark never claims a match the
 * server did not make.
 */
export type RowChoiceDetail = string | { text: string | undefined; searched: true } | undefined;

/**
 * One person, condensed into one cell: the avatar, the name, and a line for each detail the picker
 * has about them. A picker is read down its names, so everything else about a person sits under the
 * name rather than in a column of its own that stretches the dialog to hold it.
 */
export function RowChoicePerson(
  props: Readonly<{
    individual?: Individual;
    name: string;
    details?: ReadonlyArray<RowChoiceDetail>;
    unavailableReason?: RowChoiceUnavailableReason;
  }>,
) {
  const { individual, name, unavailableReason } = props;
  const details = (props.details ?? [])
    .map((detail) =>
      typeof detail === "object" ? { text: detail.text ?? "", searched: true } : { text: detail ?? "", searched: false },
    )
    .filter((detail) => Boolean(detail.text.trim()));

  return (
    <PersonCell>
      <IndividualAvatar individual={individual} />
      <PersonText>
        {/* Every picker searches by name; the mark follows the picker's `SearchMatchScope`. */}
        <PersonName variant="body1">
          <SearchMatch text={name} />
        </PersonName>
        {details.map((detail) => (
          <PersonDetail key={detail.text} variant="body2">
            {detail.searched ? <SearchMatch text={detail.text} /> : detail.text}
          </PersonDetail>
        ))}
        {unavailableReason && (
          <PersonUnavailableReason id={unavailableReason.id} variant="caption">
            {unavailableReason.text}
          </PersonUnavailableReason>
        )}
      </PersonText>
    </PersonCell>
  );
}

/**
 * What every people picker's table shares: a person column and a choice column, and none of the
 * chrome a table of two columns has no use for — no header over one column of names, no column
 * menus, hiding, density, full screen or sorting. The search and filter buttons went too: each picker
 * filtered manually and wired neither, so both opened a box that did nothing.
 */
export const ROW_CHOICE_TABLE_OPTIONS = {
  // Every picker holds its whole list and scrolls it — Coworkers loads more as it goes. The Network
  // and Bookmarks tabs drew a pagination bar anyway, over data that was never paged: `manualPagination`
  // with `rowCount` set to the list's own length and no page handler, so each page held every row and
  // the page size changed nothing. It also ran past the edge of a 320px side panel.
  enableBottomToolbar: false,
  enablePagination: false,
  enableColumnActions: false,
  enableColumnFilters: false,
  enableColumnPinning: false,
  enableDensityToggle: false,
  enableFullScreenToggle: false,
  enableGlobalFilter: false,
  enableHiding: false,
  enableRowActions: false,
  enableRowSelection: false,
  enableSorting: false,
  enableTableHead: false,
  // The person's cell takes whatever the choice column leaves, and may shrink below its name, which
  // then ellipsizes. MRT gives every cell a minimum of its column's size, 180px by default, so two
  // columns needed 360px and a 320px side panel pushed the button past its edge. `maxWidth: 0` is
  // what lets a table cell narrower than its unbreakable text; `width: 100%` then hands it the rest
  // of the row. A column's own cell props replace these rather than merge, so the choice column's
  // are unaffected.
  muiTableBodyCellProps: { sx: { maxWidth: 0, minWidth: 0, width: "100%" } },
  // With no buttons left in it, the top toolbar holds only the progress bar and the load error's
  // banner, and needs none of the 3.5rem it keeps for buttons.
  muiTopToolbarProps: { sx: { minHeight: 0 } },
} satisfies Partial<MRT_TableOptions<MRT_RowData>>;

/** The choice column's cell: as narrow as its button, at the row's right-hand end. */
export const ROW_CHOICE_CELL_PROPS = {
  align: "right",
  // `minWidth: 0` for the same reason as the person's cell: MRT's 180px column minimum.
  sx: { minWidth: 0, whiteSpace: "nowrap", width: "1%" },
} as const;

/**
 * The row's own props: clickable, and saying so, unless it cannot be chosen.
 */
export function rowChoiceRowProps(onChoose: (() => void) | undefined) {
  return {
    onClick: onChoose,
    sx: { cursor: onChoose ? "pointer" : "default" },
  };
}

/**
 * The choice itself. `name` is who it chooses, which is what tells one row's button from the next to
 * a screen reader. Without `onChoose` the row cannot be chosen and the button is disabled; it says
 * "On Team" only when `alreadyChosen` says that is why, and not for a row whose person has not loaded.
 */
export function RowChoiceButton(
  props: Readonly<{ name: string; onChoose?: () => void; alreadyChosen?: boolean; describedBy?: string }>,
) {
  const { name, onChoose } = props;
  const label = useRowChoiceLabel();
  const chosen = Boolean(props.alreadyChosen);

  // The row underneath chooses too. Letting both fire would choose twice.
  function handleClick(event: MouseEvent) {
    event.stopPropagation();
    onChoose?.();
  }

  return (
    <Button
      aria-label={chosen ? `${name} is ${ROW_ALREADY_CHOSEN_LABEL.toLowerCase()}` : `${label} ${name}`}
      aria-describedby={props.describedBy}
      disabled={chosen || onChoose === undefined}
      size="small"
      variant="outlined"
      onClick={handleClick}
    >
      {chosen ? ROW_ALREADY_CHOSEN_LABEL : label}
    </Button>
  );
}
